import type { AccountTransaction, Order } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useOrderApi } from '@hero/api-hooks';
import { useOrderDrawerStore } from '@hero/hooks-shared';
import { OrderCard } from '../../Order/Card/OrderCard';

export interface AccountTransactionOrdersTabProps {
  transaction: AccountTransaction;
}

/**
 * Linked-order tab for an `AccountTransaction`.
 *
 * Unlike the legacy `Transaction.orders[]` shape, the bilateral
 * `AccountTransaction.order` field is a SINGLE optional snapshot
 * (the per-leg backlink, set on `leg_capture` / `leg_void` /
 * `leg_adjustment`). For multi-invoice settles the `order` field
 * is undefined and the per-leg detail lives in `invoices[]` — see
 * `AccountTransactionInvoicesTab`. We surface that explicitly to
 * the user instead of showing an empty list.
 *
 * The order doc itself is fetched via `useOrderApi` so the card
 * matches the canonical Order surface (status, ref, destinations,
 * driver). Opening the card hands off to the global
 * `OrderDrawer` mounted once at the host page.
 */
export function AccountTransactionOrdersTab({
  transaction,
}: AccountTransactionOrdersTabProps) {
  const { t } = useTranslation(['m-account-transaction', 'm-payment']);
  const openOrderDrawer = useOrderDrawerStore(s => s.open);

  const linkedOrderId = transaction.order?._id;
  const { data: order, isPending } = useOrderApi(linkedOrderId);

  if (!linkedOrderId) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-sm text-muted-foreground text-center gap-2">
        <p>
          {t('m-account-transaction:drawer.orders.no_linked_order', {
            defaultValue:
              'This transaction settles multiple invoices — see the Invoices tab for the per-leg detail.',
          })}
        </p>
      </div>
    );
  }

  if (isPending && !order) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        {t('m-payment:transaction.drawer.orders.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        {t('m-account-transaction:drawer.orders.not_found', {
          defaultValue: 'Linked order is no longer available.',
        })}
      </div>
    );
  }

  const handleClick = (clickedOrder: Order) => {
    openOrderDrawer(String(clickedOrder._id));
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      <OrderCard order={order} displayForUser="carrier" onClick={handleClick} />
    </div>
  );
}
